import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";

import api from "../../services/api";
import StatusChip from "../StatusChip";

/**
 * O que o cliente tem de comercial e financeiro, numa tela só.
 *
 * Antes, para saber se um cliente tinha negócio aberto, proposta pendente ou
 * conta vencida, era preciso abrir cinco telas e filtrar por ele em cada uma.
 * Como ninguém faz isso antes de atender, a informação existia e não era usada.
 *
 * Cada seção consulta o endpoint que já existe para aquela entidade, em vez de
 * um endpoint novo que juntasse tudo. Isso preserva a permissão de cada módulo:
 * quem não tem `finance.read` recebe 403 nas contas a receber e simplesmente
 * não vê aquela seção — sem que este componente precise saber de permissões.
 */

const useStyles = makeStyles((theme) => ({
  secao: { padding: theme.spacing(2, 2, 1) },
  tituloSecao: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  contagem: { color: theme.palette.text.secondary },
  vazio: {
    color: theme.palette.text.hint,
    fontSize: 13,
    padding: theme.spacing(0, 2, 1),
  },
  linha: { cursor: "pointer" },
  centro: { padding: theme.spacing(6), textAlign: "center" },
  numero: { fontVariantNumeric: "tabular-nums" },
}));

const dinheiro = (v, moeda = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: moeda });

const data = (d) =>
  d
    ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR")
    : "—";

const PEDIDO = {
  draft: { label: "Rascunho", tone: "neutral" },
  confirmed: { label: "Confirmado", tone: "info" },
  invoiced: { label: "Faturado", tone: "warning" },
  delivered: { label: "Entregue", tone: "success" },
  canceled: { label: "Cancelado", tone: "danger" },
};

const PROPOSTA = {
  draft: { label: "Rascunho", tone: "neutral" },
  sent: { label: "Enviada", tone: "info" },
  viewed: { label: "Visualizada", tone: "warning" },
  accepted: { label: "Aceita", tone: "success" },
  rejected: { label: "Recusada", tone: "danger" },
  expired: { label: "Vencida", tone: "warning" },
  canceled: { label: "Cancelada", tone: "neutral" },
};

const CONTRATO = {
  draft: { label: "Rascunho", tone: "neutral" },
  active: { label: "Vigente", tone: "success" },
  expiring: { label: "Vencendo", tone: "warning" },
  expired: { label: "Vencido", tone: "danger" },
  renewed: { label: "Renovado", tone: "info" },
  terminated: { label: "Rescindido", tone: "neutral" },
};

const ASSINATURA = {
  trialing: { label: "Em teste", tone: "info" },
  active: { label: "Ativa", tone: "success" },
  past_due: { label: "Em atraso", tone: "warning" },
  paused: { label: "Pausada", tone: "neutral" },
  canceled: { label: "Cancelada", tone: "danger" },
};

// Assinatura sem plano cadastrado é comum aqui: o valor mora na própria
// assinatura e o catálogo de planos pode nem existir. Sem esta reserva, a
// primeira coluna sairia vazia e a linha não diria o que é.
const PERIODO = {
  monthly: "Mensal",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const NEGOCIO = {
  open: { label: "Em aberto", tone: "info" },
  won: { label: "Ganho", tone: "success" },
  lost: { label: "Perdido", tone: "danger" },
};

const chip = (mapa, valor) => {
  const meta = mapa[valor] || { label: valor || "—", tone: "neutral" };
  return <StatusChip dot tone={meta.tone} label={meta.label} />;
};

const ClientCommercial = ({ clientId, onCounts }) => {
  const classes = useStyles();
  const history = useHistory();
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState({
    negocios: [],
    propostas: [],
    pedidos: [],
    contratos: [],
    assinaturas: [],
    recebiveis: [],
  });

  const buscar = useCallback(async () => {
    if (!clientId) return;
    setCarregando(true);

    // Uma falha isolada não pode esvaziar a tela inteira: 403 por permissão em
    // um módulo é resposta legítima, não erro de carregamento.
    const pegar = (url, params, chave) =>
      api
        .get(url, { params })
        .then(({ data: d }) => (chave ? d?.[chave] : d) || [])
        .catch(() => []);

    const p = { clientId, pageSize: 50 };
    const [negocios, propostas, pedidos, contratos, assinaturas, recebiveis] =
      await Promise.all([
        pegar("/deals", { ...p, limit: 50 }, "data"),
        pegar("/proposals", p, "proposals"),
        pegar("/orders", p, "orders"),
        pegar("/contracts", p, "contracts"),
        pegar("/subscriptions", p, "subscriptions"),
        pegar("/finance/receivables", p, "data"),
      ]);

    const novo = {
      negocios,
      propostas,
      pedidos,
      contratos,
      assinaturas,
      recebiveis,
    };
    setDados(novo);
    setCarregando(false);
    if (onCounts) {
      onCounts(Object.values(novo).reduce((s, a) => s + a.length, 0));
    }
  }, [clientId, onCounts]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  if (carregando) {
    return (
      <div className={classes.centro}>
        <CircularProgress size={28} />
      </div>
    );
  }

  const total = Object.values(dados).reduce((s, a) => s + a.length, 0);
  if (!total) {
    return (
      <Box className={classes.centro}>
        <Typography variant="body2" color="textSecondary">
          Este cliente ainda não tem negócios, propostas, pedidos, contratos,
          assinaturas nem contas a receber.
        </Typography>
      </Box>
    );
  }

  const Secao = ({ titulo, itens, colunas, linha, aoClicar }) => {
    if (!itens.length) {
      return (
        <div className={classes.secao}>
          <div className={classes.tituloSecao}>
            <Typography variant="subtitle2">{titulo}</Typography>
            <Typography variant="caption" className={classes.contagem}>
              nenhum
            </Typography>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.secao}>
        <div className={classes.tituloSecao}>
          <Typography variant="subtitle2">{titulo}</Typography>
          <Typography variant="caption" className={classes.contagem}>
            {itens.length}
          </Typography>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              {colunas.map((c) => (
                <TableCell key={c.k} align={c.align || "left"}>
                  {c.k}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {itens.map((it) => (
              <TableRow
                key={it.id}
                hover
                className={aoClicar ? classes.linha : undefined}
                onClick={aoClicar ? () => aoClicar(it) : undefined}
              >
                {linha(it)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div>
      <Secao
        titulo="Negócios"
        itens={dados.negocios}
        aoClicar={() => history.push("/deals")}
        colunas={[
          { k: "Título" },
          { k: "Funil" },
          { k: "Estágio" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(d) => (
          <>
            <TableCell>{d.title}</TableCell>
            <TableCell>{d.pipeline?.name || "—"}</TableCell>
            <TableCell>{d.stage?.name || "—"}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(d.value, d.currency)}
            </TableCell>
            <TableCell align="center">{chip(NEGOCIO, d.status)}</TableCell>
          </>
        )}
      />

      <Secao
        titulo="Propostas"
        itens={dados.propostas}
        aoClicar={() => history.push("/proposals")}
        colunas={[
          { k: "Número" },
          { k: "Título" },
          { k: "Validade" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(p) => (
          <>
            <TableCell className={classes.numero}>{p.number}</TableCell>
            <TableCell>{p.title}</TableCell>
            <TableCell>{data(p.validUntil)}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(p.totalAmount, p.currency)}
            </TableCell>
            <TableCell align="center">{chip(PROPOSTA, p.status)}</TableCell>
          </>
        )}
      />

      <Secao
        titulo="Pedidos"
        itens={dados.pedidos}
        aoClicar={() => history.push("/orders")}
        colunas={[
          { k: "Número" },
          { k: "Emissão" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(o) => (
          <>
            <TableCell className={classes.numero}>{o.number}</TableCell>
            <TableCell>{data(o.createdAt)}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(o.totalAmount, o.currency)}
            </TableCell>
            <TableCell align="center">{chip(PEDIDO, o.status)}</TableCell>
          </>
        )}
      />

      <Secao
        titulo="Contratos"
        itens={dados.contratos}
        aoClicar={() => history.push("/contracts")}
        colunas={[
          { k: "Número" },
          { k: "Vigência até" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(c) => (
          <>
            <TableCell className={classes.numero}>{c.number}</TableCell>
            <TableCell>{data(c.endDate)}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(c.totalAmount ?? c.value, c.currency)}
            </TableCell>
            <TableCell align="center">{chip(CONTRATO, c.status)}</TableCell>
          </>
        )}
      />

      <Secao
        titulo="Assinaturas"
        itens={dados.assinaturas}
        aoClicar={() => history.push("/subscriptions")}
        colunas={[
          { k: "Plano" },
          { k: "Próxima cobrança" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(s) => (
          <>
            <TableCell>
              {s.plan?.name ||
                s.planName ||
                PERIODO[s.billingPeriod] ||
                "Avulsa"}
            </TableCell>
            <TableCell>{data(s.nextBillingAt || s.currentPeriodEnd)}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(s.amount ?? s.plan?.amount, s.currency)}
            </TableCell>
            <TableCell align="center">{chip(ASSINATURA, s.status)}</TableCell>
          </>
        )}
      />

      <Secao
        titulo="Contas a receber"
        itens={dados.recebiveis}
        aoClicar={() => history.push("/finance/receivables")}
        colunas={[
          { k: "Descrição" },
          { k: "Vencimento" },
          { k: "Valor", align: "right" },
          { k: "Situação", align: "center" },
        ]}
        linha={(r) => (
          <>
            <TableCell>{r.description || "—"}</TableCell>
            <TableCell>{data(r.dueDate)}</TableCell>
            <TableCell align="right" className={classes.numero}>
              {dinheiro(r.amount, r.currency)}
            </TableCell>
            <TableCell align="center">
              <StatusChip dot status={r.status} />
            </TableCell>
          </>
        )}
      />
    </div>
  );
};

export default ClientCommercial;
